import { Alert, useToast } from '@razorpay/blade/components';
import api from 'api';
import useSalaryComponentsList from 'components/RunPayroll/Popups/EditSalary/hooks/useSalaryComponentsList';
import { DeductionsComponentType } from 'components/SalaryComponents/types';
import { useSearchParams } from 'hooks/useSearchParams';
import React, { useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import loggedInUserSelectors from 'reducers/loggedInUser/selectors';
import { AppError } from 'utils/AppError';
import RecurringDeductionsBreakdown from '../components/RecurringDeductionsBreakdown';
import RecurringDeductionsModal from '../components/RecurringDeductionsModal';
import DeleteModal from '../components/generics/DeleteModal';
import { queryKeys } from '../constants';
import useListDeductions from '../hooks/useListDeductions';
import { DeductionData, RecurringDeductionsData, RecurringDeductionsFormData } from '../types';
import { getDeductionsInPaise, getPayslipUrl } from '../utils';
import useHasPermission from 'utils/useHasPermission';
import permissions from 'constants/permissions';
import { usePayDetails } from '../context/PayDetailsContext';

const RecurringDeductionsView = () => {
  const [isDeductionsModalOpen, setIsDeductionsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const { urlParams } = useSearchParams();
  const userId = urlParams.get('userId');
  const loggedInUserId = useSelector(loggedInUserSelectors.peopleId);
  const orgId = useSelector(loggedInUserSelectors.organizationID);
  const peopleId = String(userId ?? loggedInUserId);
  const toast = useToast();
  const queryClient = useQueryClient();
  const hasPayrollEditPermission = useHasPermission([permissions.PAYROLL_EDIT]);

  // Fetch initial data from API
  const {
    data: deductionsData = [],
    error,
    isFetching,
    refetch,
  } = useListDeductions({
    userId: peopleId,
    isEnabled: hasPayrollEditPermission,
  });

  const { compensationData } = usePayDetails();

  //For non-payroll edit permission users, we show the recurring deductions from the compensation data
  const data = hasPayrollEditPermission ? deductionsData : compensationData.recurringDeductions;

  // Add this hook to fetch component details
  const { searchSalaryComponents, getSalaryComponentById, isFetchingSalaryComponents } =
    useSalaryComponentsList<DeductionsComponentType>({
      requestParams: {
        category: 'DEDUCTIONS_CATEGORY',
        payType: 'LIST_RECURRING_TYPE',
        status: 'COMPONENT_STATUS_ACTIVE',
      },
    });

  const handleViewPayslip = () => {
    if (peopleId && orgId) {
      const payslipUrl = getPayslipUrl(peopleId, orgId);
      window.open(payslipUrl, '_blank');
    }
  };

  const updateDeductions = useMutation({
    mutationFn: api.salaryComponents.mutations.updateDeductions,
    onError: (error: AppError) => {
      toast.show({
        color: 'negative',
        content: error.message || 'Failed to update deduction. Please try again.',
        autoDismiss: true,
        duration: 5000,
      });
    },
  });

  const existingDeductionsIdList = data
    .filter((deduction) => selectedComponentId !== deduction.componentId)
    .map((deduction) => deduction.componentId);

  const onDismiss = () => {
    setIsDeductionsModalOpen(false);
    setSelectedComponentId(null);
  };

  const onDismissDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedComponentId(null);
  };

  const onDelete = () => {
    if (selectedComponentId) {
      const idsForDeletion = data
        .filter(
          (deduction: RecurringDeductionsData) => deduction.componentId === selectedComponentId,
        )
        .map((deduction: RecurringDeductionsData) => deduction.id);

      // Update in the API with just the remaining deductions
      updateDeductions.mutate(
        {
          assignments: [
            { userId: peopleId, deductions: getDeductionsInPaise(data), idsForDeletion },
          ],
        },
        {
          onSuccess: () => {
            setIsDeleteModalOpen(false);
            setSelectedComponentId(null);
            queryClient.invalidateQueries([queryKeys.FETCH_LIST_DEDUCTIONS]);
            queryClient.invalidateQueries([queryKeys.FETCH_COMPENSATION_DETAILS]);
            toast.show({
              color: 'information',
              content: 'Recurring deduction deleted successfully',
              autoDismiss: true,
              duration: 5000,
              action: {
                text: 'View change in payslip',
                onClick: handleViewPayslip,
              },
            });
          },
        },
      );
    } else {
      // Just close the modal if no selection
      setIsDeleteModalOpen(false);
      setSelectedComponentId(null);
    }
  };

  const onEdit = (formData: RecurringDeductionsFormData) => {
    // Get the component details to display the name
    const componentDetails = getSalaryComponentById(formData.componentId);
    const componentName = componentDetails?.name || 'New Deduction';

    // Update existing deduction
    const updatedDeductions = data.map((deduction: DeductionData) => {
      if (deduction.componentId === selectedComponentId) {
        return {
          componentId: formData.componentId,
          componentName: componentName,
          monthlyAmount: formData.monthlyAmount,
        };
      }
      return deduction;
    });

    updateDeductions.mutate(
      {
        assignments: [
          {
            userId: peopleId,
            deductions: getDeductionsInPaise(updatedDeductions),
            idsForDeletion: [],
          },
        ],
      },
      {
        onSuccess: () => {
          setIsDeductionsModalOpen(false);
          setSelectedComponentId(null);
          queryClient.invalidateQueries([queryKeys.FETCH_LIST_DEDUCTIONS]);
          queryClient.invalidateQueries([queryKeys.FETCH_COMPENSATION_DETAILS]);
          toast.show({
            color: 'positive',
            content: 'Recurring deduction updated successfully',
            autoDismiss: true,
            duration: 5000,
            action: {
              text: 'View change in payslip',
              onClick: handleViewPayslip,
            },
          });
        },
      },
    );
  };

  const onCreate = (formData: RecurringDeductionsFormData) => {
    // Get the component details to display the name
    const componentDetails = getSalaryComponentById(formData.componentId);
    const componentName = componentDetails?.name || 'New Deduction';

    const updatedDeductions = [
      ...data,
      {
        componentId: formData.componentId,
        componentName: componentName,
        monthlyAmount: formData.monthlyAmount,
      },
    ];

    updateDeductions.mutate(
      {
        assignments: [
          {
            userId: peopleId,
            deductions: getDeductionsInPaise(updatedDeductions),
            idsForDeletion: [],
          },
        ],
      },
      {
        onSuccess: () => {
          setIsDeductionsModalOpen(false);
          setSelectedComponentId(null);
          queryClient.invalidateQueries([queryKeys.FETCH_LIST_DEDUCTIONS]);
          queryClient.invalidateQueries([queryKeys.FETCH_COMPENSATION_DETAILS]);
          toast.show({
            color: 'positive',
            content: 'Recurring deduction added successfully',
            autoDismiss: true,
            duration: 5000,
            action: {
              text: 'View change in payslip',
              onClick: handleViewPayslip,
            },
          });
        },
      },
    );
  };

  const onSubmit = (formData: RecurringDeductionsFormData) => {
    const isEdit = !!selectedComponentId;
    if (isEdit) {
      onEdit(formData);
    } else {
      onCreate(formData);
    }
  };

  const selectedComponent = data.find(
    (item: RecurringDeductionsData) => item.componentId === selectedComponentId,
  ) || {
    componentId: '',
    componentName: '',
  };

  return (
    <>
      {error && (
        <Alert
        color="negative"
        description={error?.message || 'An error occurred while fetching deductions'}
        actions={{
          primary: {
            text: 'Retry',
            onClick: refetch,
          },
        }}
        isFullWidth
      />)}
      <RecurringDeductionsBreakdown
        data={data}
        setSelectedComponentId={setSelectedComponentId}
        openDeductionModal={() => setIsDeductionsModalOpen(true)}
        openDeleteModal={() => setIsDeleteModalOpen(true)}
        isAddDeductionAllowed={searchSalaryComponents(existingDeductionsIdList).length > 0}
        isLoading={isFetching || isFetchingSalaryComponents}
      />
      <RecurringDeductionsModal
        key={`deduction-modal-${selectedComponentId || 'new'}-${isDeductionsModalOpen}`}
        initialFormData={{
          componentId: selectedComponent.componentId,
          monthlyAmount: selectedComponentId
            ? (selectedComponent as RecurringDeductionsData).monthlyAmount
            : undefined,
        }}
        isOpen={isDeductionsModalOpen}
        isEdit={!!selectedComponentId}
        onDismiss={onDismiss}
        onSubmit={onSubmit}
        existingDeductionsIdList={existingDeductionsIdList}
      />
      <DeleteModal
        componentName="Deduction"
        description={`You are about to delete the recurring deduction '${
          selectedComponent.componentName || 'Selected deduction'
        }'`}
        descriptionOnAction="Please note that past employee payslip and salary register will remain unchanged"
        isOpen={isDeleteModalOpen}
        onDismiss={onDismissDeleteModal}
        onSubmit={onDelete}
      />
    </>
  );
};

export default RecurringDeductionsView;
